import jsQR from 'jsqr';

/**
 * Reads the QR code from an image the user picked (e.g. a screenshot of
 * their bank app's "Receive money" KHQR). Everything happens on the device;
 * only the decoded text is sent to the server.
 */
export async function decodeQrFromImage(file: File): Promise<string | null> {
  const bitmap = await loadImage(file);
  // Screenshots are large; try a few sizes since jsQR works best when the
  // code is neither tiny nor huge.
  for (const maxSide of [1000, 1600, 700, 2400]) {
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;
    context.drawImage(bitmap, 0, 0, width, height);
    const { data } = context.getImageData(0, 0, width, height);
    const result = jsQR(data, width, height, { inversionAttempts: 'attemptBoth' });
    if (result?.data) return result.data;
  }
  return null;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read the image'));
    };
    image.src = url;
  });
}
